/**
 * ThemeProvider: single source of truth for the UI appearance theme.
 *
 * Exposes the user's choice ("light" | "dark" | "system"), the resolved theme
 * actually rendered ("light" | "dark"), and a setter. Persists the choice to
 * localStorage and reflects the resolved theme onto <html data-theme> +
 * color-scheme so the CSS custom properties in tokens.css apply.
 *
 * A dependency-free bootstrap script in index.html applies the same resolution
 * before first paint to avoid a flash of the wrong theme; this provider takes
 * over once React mounts and keeps things in sync (storage + OS preference).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ThemeChoice = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

/** localStorage key. Must match the bootstrap script in index.html. */
export const THEME_STORAGE_KEY = "marketplace-theme";

const DARK_QUERY = "(prefers-color-scheme: dark)";

function readStoredChoice(): ThemeChoice {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") {
      return stored;
    }
  } catch {
    // localStorage unavailable (private mode, etc.) — fall through to default.
  }
  return "system";
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(DARK_QUERY).matches
  );
}

function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  if (choice === "light" || choice === "dark") return choice;
  return systemPrefersDark() ? "dark" : "light";
}

interface ThemeContextValue {
  /** What the user selected. "system" follows the OS preference. */
  choice: ThemeChoice;
  /** The theme actually applied right now. */
  resolvedTheme: ResolvedTheme;
  /** Update the choice; persists to localStorage and re-applies. */
  setChoice: (choice: ThemeChoice) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(readStoredChoice);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(readStoredChoice()),
  );

  // Apply the resolved theme to <html> whenever the choice changes. The
  // resolvedTheme state is the source of truth for the matchMedia listener
  // below, so recompute and store it here too.
  useEffect(() => {
    const resolved = resolveTheme(choice);
    setResolvedTheme(resolved);
    document.documentElement.dataset.theme = resolved;
  }, [choice]);

  // While following the OS preference, react to live changes (e.g. the user
  // flips their system to dark mode). Only active when choice is "system".
  useEffect(() => {
    if (choice !== "system" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(DARK_QUERY);
    const onChange = (e: MediaQueryListEvent) => {
      const resolved: ResolvedTheme = e.matches ? "dark" : "light";
      setResolvedTheme(resolved);
      document.documentElement.dataset.theme = resolved;
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [choice]);

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Persistence is best-effort; the in-memory choice still applies.
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ choice, resolvedTheme, setChoice }),
    [choice, resolvedTheme, setChoice],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return ctx;
}
